import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Card, Col, Form, Row } from '@themesberg/react-bootstrap'
import Chartist from 'react-chartist'
import { useCookies } from 'react-cookie'
import { toast } from 'react-toastify'
import { getTicketSourceSummaryByDate } from '../../../controller/api/statistic/ticketSourceSummary'

const toNumber = (value) => {
  const num = Number(value || 0)
  return Number.isNaN(num) ? 0 : num
}

const percent = (value, total) => {
  if (!total) return '0.00%'
  return `${((value / total) * 100).toFixed(2)}%`
}

const formatValue = (value) => {
  return new Intl.NumberFormat('vn-VI', {}).format(toNumber(value))
}

export default function TicketSourceSummary({ embedded = false, summaryType = 'vietlott', region = '', hideGameFilter = false }) {
  const [cookies] = useCookies(['lottmekong'])
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [selectedGameId, setSelectedGameId] = useState('')
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState({
    totalTicket: { web: 0, app: 0, undefined: 0 },
  })

  const fetchData = useCallback(async (payload) => {
    setLoading(true)
    try {
      const response = await getTicketSourceSummaryByDate(cookies.lottmekong, payload)
      if (response) {
        setData({
          totalTicket: response.totalTicket || { web: 0, app: 0, undefined: 0 },
        })
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Không thể tải dữ liệu')
    } finally {
      setLoading(false)
    }
  }, [cookies.lottmekong])

  const handleSearch = useCallback(() => {
    if (!selectedDate) {
      toast.warning('Vui lòng chọn ngày')
      return
    }
    fetchData({
      from: selectedDate,
      to: selectedDate,
      gameId: selectedGameId,
      summaryType,
      region,
    })
  }, [fetchData, selectedDate, selectedGameId, summaryType, region])

  useEffect(() => {
    if (!embedded) {
      document.title = 'Thống kê vé theo nguồn'
    }
    fetchData({
      from: selectedDate,
      to: selectedDate,
      gameId: selectedGameId,
      summaryType,
      region,
    })
  }, [fetchData, embedded, summaryType, region, selectedGameId, selectedDate])

  const totalRow = useMemo(() => {
    const web = toNumber(data.totalTicket?.web)
    const app = toNumber(data.totalTicket?.app)
    const undefinedSource = toNumber(data.totalTicket?.undefined)
    const total = web + app + undefinedSource
    return {
      web,
      app,
      undefinedSource,
      total,
      webPercent: percent(web, total),
      appPercent: percent(app, total),
      undefinedPercent: percent(undefinedSource, total),
    }
  }, [data])

  const pieData = useMemo(() => ({
    series: [
      { value: totalRow.web, className: 'ct-series-web' },
      { value: totalRow.app, className: 'ct-series-app' },
      { value: totalRow.undefinedSource, className: 'ct-series-undefined' },
    ],
  }), [totalRow.web, totalRow.app, totalRow.undefinedSource])

  const pieOptions = useMemo(() => ({
    donut: true,
    donutSolid: true,
    donutWidth: embedded ? 26 : 35,
    showLabel: false,
  }), [embedded])

  return (
    <Row className="g-3">
      <Col xs={12}>
        <Card border="light" className="shadow-sm">
          {embedded && (
            <Card.Header className="py-2">
              <h6 className="mb-0">Bộ lọc thống kê vé</h6>
            </Card.Header>
          )}
          <Card.Body className={embedded ? 'py-3' : ''}>
            <Row className="align-items-end">
              <Col sm={12} md={hideGameFilter ? 6 : 4}>
                <Form.Label>Ngày thống kê</Form.Label>
                <Form.Control
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              </Col>
              {!hideGameFilter && (
                <Col sm={12} md={4}>
                  <Form.Label>Trò chơi</Form.Label>
                  <Form.Select
                    value={selectedGameId}
                    onChange={(e) => setSelectedGameId(e.target.value)}
                  >
                    <option value="">Tất cả trò chơi</option>
                    <option value="lotto535">LOTO 5/35</option>
                    <option value="keno">Keno</option>
                    <option value="mega">Mega 6/45</option>
                    <option value="power">Power 6/55</option>
                    <option value="max3d">Max3D/3D+</option>
                    <option value="max3dpro">Max3D Pro</option>
                  </Form.Select>
                </Col>
              )}
              <Col sm={12} md={hideGameFilter ? 6 : 4} className="text-md-end mt-3 mt-md-0">
                <Button variant="primary" onClick={handleSearch}>Tìm kiếm</Button>
              </Col>
            </Row>
            <div className="small text-muted mt-2">Hiển thị dữ liệu theo từng ngày</div>
          </Card.Body>
        </Card>
      </Col>
      <Col xs={12}>
        <Card border="light" className="shadow-sm">
          {embedded && (
            <Card.Header className="py-2">
              <h6 className="mb-0">Phân bổ vé WEB/APP</h6>
            </Card.Header>
          )}
          <Card.Body style={{ paddingTop: embedded ? 14 : 24, paddingBottom: embedded ? 14 : 24 }}>
            <div className={`d-flex justify-content-between align-items-start ${embedded ? 'mb-2' : 'mb-3'}`}>
              <div>
              <h6 className={`mb-1 ${embedded ? 'fs-6' : ''}`}>Tổng vé theo kênh WEB/APP/Ko xác định</h6>
                <div className="small text-muted">Tỷ trọng số vé trong ngày đã chọn</div>
              </div>
              {!loading && (
                <div className="text-end">
                  <div className="small text-muted">Tổng số vé</div>
                  <div className={`${embedded ? 'h5' : 'h4'} mb-0 fw-bold`}>{formatValue(totalRow.total)}</div>
                </div>
              )}
            </div>
            {loading ? (
              <h5 className="text-center mb-0">Đang tải dữ liệu...</h5>
            ) : (
              <Row className="align-items-center">
                <Col sm={12} md={6}>
                  <div className={`ticket-source-pie ${embedded ? 'small-chart' : ''}`}>
                    <Chartist data={pieData} options={pieOptions} type="Pie" className="ct-golden-section" />
                  </div>
                </Col>
                <Col sm={12} md={6}>
                  <div
                    className={`mb-2 ${embedded ? 'p-2' : 'p-3'}`}
                    style={{ background: '#f8f9fa', borderRadius: 10, border: '1px solid #edf2f7' }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: '#0d6efd',
                            marginRight: 8
                          }}
                        />
                        <span className="fw-semibold">WEB</span>
                      </div>
                      <div className="text-end">
                        <div className="fw-bold">{formatValue(totalRow.web)}</div>
                        <div className="small text-muted">{totalRow.webPercent}</div>
                      </div>
                    </div>
                  </div>
                  <div
                    className={embedded ? 'p-2' : 'p-3'}
                    style={{ background: '#f8f9fa', borderRadius: 10, border: '1px solid #edf2f7' }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: '#dc3545',
                            marginRight: 8
                          }}
                        />
                        <span className="fw-semibold">APP</span>
                      </div>
                      <div className="text-end">
                        <div className="fw-bold">{formatValue(totalRow.app)}</div>
                        <div className="small text-muted">{totalRow.appPercent}</div>
                      </div>
                    </div>
                  </div>
                  <div
                    className={`mt-2 ${embedded ? 'p-2' : 'p-3'}`}
                    style={{ background: '#f8f9fa', borderRadius: 10, border: '1px solid #edf2f7' }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: '#6c757d',
                            marginRight: 8
                          }}
                        />
                        <span className="fw-semibold">Ko xác định</span>
                      </div>
                      <div className="text-end">
                        <div className="fw-bold">{formatValue(totalRow.undefinedSource)}</div>
                        <div className="small text-muted">{totalRow.undefinedPercent}</div>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}
